import React, { useState } from "react";
import CommonTableTwoLine from "../../../components/common/common-table/common-table-two-line";
import { ApiRequest } from "../../../services/api/api";
import { DatePicker, Button, Space, Row, Col, Card, Statistic } from "antd";
import moment from "moment";
import { ArrowUpOutlined, ArrowDownOutlined, MinusOutlined } from "@ant-design/icons";
import "./slow-moving-stock-report-page.css";

interface SlowMovingStockSummaryData {
  reportDate: string;
  totalInventoryValue: number;
  normalStockValue: number;
  slowMovingValue: number;
  excessStockValue: number;
  deadStockValue: number;
  customerSpecificValue: number;
}

interface SlowMovingStockDetailData {
  reportDate: string;
  productTypeId: number;
  productTypeName: string;
  productCode: string;
  productDescription: string;
  customerName: string;
  isCustomerSpecific: boolean;
  customerId: number;
  quantityInStock: number;
  stockValueNzd: number;
  lastSaleDate: string;
  avgMonthlyUsage: number;
  monthsOfStock: number;
  status: string;
  recommendedAction: string;
  productId: number;
  baseProductId: number;
  firstStockDate: string;
  last6MonthTotalUsage: number;
  noSalesMonths: number;
}

const SlowMovingStockReportPage: React.FC = () => {
  const [summaryData, setSummaryData] = useState<SlowMovingStockSummaryData | null>(null);
  const [detailData, setDetailData] = useState<SlowMovingStockDetailData[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState<moment.Moment>(moment());

  const fetchSummaryData = async (date: moment.Moment) => {
    try {
      // 将日期调整为该周的周一
      const mondayDate = date.clone().startOf('week').add(1, 'day');
      console.log("Fetching slow moving stock summary for:", mondayDate.format("YYYY-MM-DD"));
      const result = await ApiRequest({
        otherUrl: "SlowMovingStockSnapshot/summary",
        method: "get",
        params: { reportDate: mondayDate.format("YYYY-MM-DD") },
        isShowSpinner: true,
      });
      console.log("Slow moving stock summary result:", result);
      console.log("Result data:", result?.data);
      // 数据直接在result.data中
      const data = result?.data || null;
      console.log("Set summary data:", data);
      setSummaryData(data);
    } catch (error: any) {
      console.error("Failed to load slow moving stock summary", error);
      setSummaryData(null);
    }
  };

  const fetchDetailData = async (date: moment.Moment) => {
    try {
      // 将日期调整为该周的周一
      const mondayDate = date.clone().startOf('week').add(1, 'day');
      console.log("Fetching slow moving stock detail for:", mondayDate.format("YYYY-MM-DD"));
      const result = await ApiRequest({
        otherUrl: "SlowMovingStockSnapshot/detail",
        method: "get",
        params: { reportDate: mondayDate.format("YYYY-MM-DD") },
        isShowSpinner: true,
      });
      console.log("Slow moving stock detail result:", result);
      console.log("Detail result data:", result?.data);
      // 数据直接在result.data中
      const data = result?.data || [];
      console.log("Set detail data:", data);

      // 对数据进行排序：按 status，然后按产品价值降序，最后按 categories (productTypeName)
      const sortedData = [...data].sort((a: any, b: any) => {
        // 首先按 status 排序
        const statusOrder: { [key: string]: number } = {
          'obsolete': 0,  // 死库存排最前
          'excess': 1,    // 过剩库存
          'slow_moving': 2, // 慢动库存
          'normal': 3     // 正常库存排最后
        };

        const aStatusOrder = statusOrder[a.status] ?? 999;
        const bStatusOrder = statusOrder[b.status] ?? 999;

        if (aStatusOrder !== bStatusOrder) {
          return aStatusOrder - bStatusOrder;
        }

        // 如果 status 相同，则按产品价值降序排列
        const aValue = a.stockValueNzd || 0;
        const bValue = b.stockValueNzd || 0;

        if (aValue !== bValue) {
          return bValue - aValue; // 降序排列
        }

        // 如果产品价值也相同，则按 categories (productTypeName) 排序
        const aCategory = (a.productTypeName || '').toLowerCase();
        const bCategory = (b.productTypeName || '').toLowerCase();

        return aCategory.localeCompare(bCategory);
      });

      setDetailData(sortedData);
    } catch (error: any) {
      console.error("Failed to load slow moving stock detail", error);
      setDetailData([]);
    }
  };

  const fetchAllData = async (date: moment.Moment) => {
    setLoading(true);
    await Promise.all([fetchSummaryData(date), fetchDetailData(date)]);
    setLoading(false);
  };

  React.useEffect(() => {
    fetchAllData(selectedDate);
  }, []);

  const handleDateChange = (date: moment.Moment | null) => {
    if (date) {
      setSelectedDate(date);
      fetchAllData(date);
    }
  };

  const getStatusColor = (status: string) => {
    const colorMap: { [key: string]: string } = {
      'normal': '#52c41a',
      'slow_moving': '#fadb14',
      'excess': '#fa8c16',
      'obsolete': '#f5222d'
    };
    return colorMap[status] || '#8c8c8c';
  };

  const getStatusName = (status: string) => {
    const nameMap: { [key: string]: string } = {
      'normal': 'Normal',
      'slow_moving': 'Slow Moving',
      'excess': 'Excess',
      'obsolete': 'Dead Stock'
    };
    return nameMap[status] || status;
  };

  const detailColumns = [

    {
      title: "Category",
      field: "productTypeName",
      editable: "never",
      render: (rowData: any) => (
        <div style={{ fontSize: '13px' }}>
          {rowData.productTypeName}
        </div>
      )
    },
    {
      title: "SKU",
      field: "productCode",
      editable: "never",
      render: (rowData: any) => (
        <div style={{ fontSize: '13px' }}>
          {rowData.productCode}
        </div>
      )
    },
    {
      title: "Product Description",
      field: "productDescription",
      editable: "never",
      render: (rowData: any) => (
        <div style={{ fontSize: '13px' }}>
          {rowData.productDescription}
        </div>
      )
    },
    {
      title: "Customer",
      field: "customerName",
      editable: "never",
      render: (rowData: any) => (
        <div style={{ fontSize: '13px' }}>
          {rowData.isCustomerSpecific ? rowData.customerName : '-'}
        </div>
      )
    },
    {
      title: "Customer Spec",
      field: "isCustomerSpecific",
      editable: "never",
      render: (rowData: any) => (
        <div style={{ textAlign: 'center', fontSize: '13px' }}>
          {rowData.isCustomerSpecific ? 'Yes' : 'No'}
        </div>
      )
    },
    {
      title: "Stock Quantity",
      field: "quantityInStock",
      editable: "never",
      type: "numeric",
      render: (rowData: any) => (
        <div style={{ textAlign: 'center', fontSize: '13px' }}>
          {rowData.quantityInStock || 0}
        </div>
      )
    },
    {
      title: "Value (NZD)",
      field: "stockValueNzd",
      editable: "never",
      render: (rowData: any) => (
        <div style={{ fontSize: '13px' }}>
          ${(rowData.stockValueNzd || 0).toFixed(2)}
        </div>
      )
    },
    {
      title: "Last Sale Date",
      field: "lastSaleDate",
      editable: "never",
      render: (rowData: any) => (
        <div style={{ fontSize: '13px' }}>
          {rowData.lastSaleDate ? moment(rowData.lastSaleDate).format("DD/MM/YYYY") : '-'}
        </div>
      )
    },
    {
      title: "6-Month Usage",
      field: "last6MonthTotalUsage",
      editable: "never",
      type: "numeric",
      render: (rowData: any) => (
        <div style={{ textAlign: 'center', fontSize: '13px' }}>
          {rowData.last6MonthTotalUsage || 0}
        </div>
      )
    },
    {
      title: "Months Of Stock",
      field: "monthsOfStock",
      editable: "never",
      type: "numeric",
      render: (rowData: any) => (
        <div style={{ textAlign: 'center', fontSize: '13px' }}>
          {rowData.monthsOfStock || 0}
        </div>
      )
    },
    {
      title: "Status",
      field: "status",
      editable: "never",
      lookup: {
        'normal': 'Normal',
        'slow_moving': 'Slow Moving',
        'excess': 'Excess',
        'obsolete': 'Dead Stock'
      },
      render: (rowData: any) => (
        <div
          style={{
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '13px',
            fontWeight: 'bold',
            backgroundColor: getStatusColor(rowData.status),
            color: '#fff',
            display: 'inline-block'
          }}
        >
          {getStatusName(rowData.status)}
        </div>
      )
    },
    // {
    //   title: "Action",
    //   field: "recommendedAction",
    //   editable: "never",
    //   render: (rowData: any) => (
    //     <div style={{ fontSize: '13px' }}>
    //       {rowData.recommendedAction}
    //     </div>
    //   )
    // }
  ];

  return (
    <div>
      <div style={{ marginBottom: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h2 style={{ margin: 0, fontSize: '18px' }}>Stock Snapshot</h2>
        <Space>
          <DatePicker
            value={selectedDate}
            onChange={handleDateChange}
            format="YYYY-MM-DD"
            placeholder="选择日期 / Select Date"
          />
          <Button
            type="primary"
            onClick={() => fetchAllData(selectedDate)}
            loading={loading}
          >
            Refresh
          </Button>
        </Space>
      </div>

      {summaryData && (
        <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Card
              bordered
              size="small"
              style={{
                borderRadius: '4px',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)',
                borderTop: '3px solid #1890ff'
              }}
              bodyStyle={{ padding: '12px' }}
            >
              <Statistic
                title="Total Inventory"
                value={summaryData.totalInventoryValue}
                precision={2}
                prefix="$"
                valueStyle={{
                  color: '#262626',
                  fontWeight: 600,
                  fontSize: '16px'
                }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Card
              bordered
              size="small"
              style={{
                borderRadius: '4px',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)',
                borderTop: '3px solid #52c41a'
              }}
              bodyStyle={{ padding: '12px' }}
            >
              <Statistic
                title="Normal Stock (< 3 Months)"
                value={summaryData.normalStockValue}
                precision={2}
                prefix="$"
                valueStyle={{
                  color: '#262626',
                  fontWeight: 600,
                  fontSize: '16px'
                }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Card
              bordered
              size="small"
              style={{
                borderRadius: '4px',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)',
                borderTop: '3px solid #fadb14'
              }}
              bodyStyle={{ padding: '12px' }}
            >
              <Statistic
                title="Slow Moving (3–12 Months)"
                value={summaryData.slowMovingValue}
                precision={2}
                prefix="$"
                valueStyle={{
                  color: '#262626',
                  fontWeight: 600,
                  fontSize: '16px'
                }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Card
              bordered
              size="small"
              style={{
                borderRadius: '4px',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)',
                borderTop: '3px solid #fa8c16'
              }}
              bodyStyle={{ padding: '12px' }}
            >
              <Statistic
                title="Excess Stock (> 12 Months)"
                value={summaryData.excessStockValue}
                precision={2}
                prefix="$"
                valueStyle={{
                  color: '#262626',
                  fontWeight: 600,
                  fontSize: '16px'
                }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Card
              bordered
              size="small"
              style={{
                borderRadius: '4px',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)',
                borderTop: '3px solid #f5222d'
              }}
              bodyStyle={{ padding: '12px' }}
            >
              <Statistic
                title="Dead Stock (> 12 Months, No Sales)"
                value={summaryData.deadStockValue}
                precision={2}
                prefix="$"
                valueStyle={{
                  color: '#262626',
                  fontWeight: 600,
                  fontSize: '16px'
                }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Card
              bordered
              size="small"
              style={{
                borderRadius: '4px',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)',
                borderTop: '3px solid #722ed1'
              }}
              bodyStyle={{ padding: '12px' }}
            >
              <Statistic
                title="Customer Specific"
                value={summaryData.customerSpecificValue}
                precision={2}
                prefix="$"
                valueStyle={{
                  color: '#262626',
                  fontWeight: 600,
                  fontSize: '16px'
                }}
              />
            </Card>
          </Col>
        </Row>
      )}

      <div className="stock-snapshot-table">
        <CommonTableTwoLine
          title={`Stock Details - ${selectedDate.clone().startOf('week').add(1, 'day').format("YYYY-MM-DD")}`}
          column={detailColumns}
          initData={detailData}
          editable={false}
          isNotAddable={true}
          isNotEditable={true}
          isNotDeletable={true}
          isExportable={true}
          defaultPageSize={100}
          defaultPageSizeOptions={[10, 20, 50, 100]}
          stickyHeader={true}
          maxBodyHeight="calc(100vh - 250px)"
        />
      </div>
    </div>
  );
};

export default SlowMovingStockReportPage;
